import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginSimple from './pages/LoginSimple';
import DashboardNew from './pages/DashboardNew';

// 🚀 LAZY LOADING: Páginas principais (code splitting)
const Funcionarios = lazy(() => import('./pages/Funcionarios'));
const PastaVirtual = lazy(() => import('./pages/PastaVirtual'));
const Qualificacoes = lazy(() => import('./pages/Qualificacoes'));
const DashboardQualificacoes = lazy(() => import('./pages/DashboardQualificacoes'));
const ReclassificacaoQualificacoes = lazy(() => import('./pages/ReclassificacaoQualificacoes'));
const QualificacoesAlertas = lazy(() =>
  import('../pages/QualificacoesAlertas').then((m) => ({ default: m.QualificacoesAlertas })),
);
const LicencasPage = lazy(() => import('./pages/LicencasPage'));
const FichaFuncionarioPage = lazy(() => import('./pages/FichaFuncionarioPage'));
const ImportacaoPageV2 = lazy(() => import('../pages/ImportacaoPageV2'));
const Simuladores = lazy(() => import('./pages/Simuladores'));
const SimuladoresDashboard = lazy(
  () => import('./pages/simuladores/dashboard/SimuladoresDashboard'),
);
const DashboardDesempenho = lazy(() => import('./pages/simuladores/dashboard/DashboardDesempenho'));
const AgendaCalendario = lazy(() => import('./pages/simuladores/agenda'));
const FichasSessao = lazy(() => import('./pages/simuladores/fichas'));
const FichaVoo = lazy(() => import('./pages/FichaVoo'));
const NovaSessao = lazy(() => import('./pages/simuladores/sessoes/nova'));
const CrudSimuladores = lazy(
  () => import('./pages/simuladores/cadastros/simuladores/crud-completo'),
);
const CrudManobras = lazy(() => import('./pages/simuladores/cadastros/manobras'));
const CrudModelos = lazy(() => import('./pages/simuladores/cadastros/modelos'));
const CrudCategorias = lazy(() => import('./pages/simuladores/cadastros/categorias'));
const CrudTiposSessao = lazy(() => import('./pages/simuladores/cadastros/tipos-sessao'));
const CrudInstrutores = lazy(() => import('./pages/simuladores/cadastros/instrutores'));
const CrudModelosSessao = lazy(() => import('./pages/simuladores/cadastros/modelos-sessao'));
const RelatoriosSimuladores = lazy(() => import('./pages/simuladores/relatorios'));
const ConfiguracoesCadastros = lazy(() => import('./pages/simuladores/cadastros/configuracoes'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const ConfiguracoesCadastrosGerais = lazy(() => import('./pages/Configuracoes/CadastrosPage'));
const ComplianceSettings = lazy(() =>
  import('./pages/ComplianceSettings').then((m) => ({ default: m.ComplianceSettings })),
);

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-sm text-gray-600">Carregando...</p>
    </div>
  </div>
);

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-right" richColors closeButton />
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginSimple />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/funcionarios"
                element={
                  <ProtectedRoute>
                    <Funcionarios />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pasta-virtual/:funcionarioId"
                element={
                  <ProtectedRoute>
                    <PastaVirtual />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qualificacoes"
                element={
                  <ProtectedRoute>
                    <Qualificacoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qualificacoes/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardQualificacoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qualificacoes/reclassificacao"
                element={
                  <ProtectedRoute>
                    <ReclassificacaoQualificacoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qualificacoes/alertas"
                element={
                  <ProtectedRoute>
                    <QualificacoesAlertas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/licencas"
                element={
                  <ProtectedRoute>
                    <LicencasPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/funcionarios/:id/ficha"
                element={
                  <ProtectedRoute>
                    <FichaFuncionarioPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores"
                element={
                  <ProtectedRoute>
                    <Simuladores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/dashboard"
                element={
                  <ProtectedRoute>
                    <SimuladoresDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/desempenho/:funcionarioId"
                element={
                  <ProtectedRoute>
                    <DashboardDesempenho />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/calendario"
                element={
                  <ProtectedRoute>
                    <AgendaCalendario />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/fichas"
                element={
                  <ProtectedRoute>
                    <FichasSessao />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/configuracoes"
                element={
                  <ProtectedRoute>
                    <ConfiguracoesCadastros />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/fichas/:id"
                element={
                  <ProtectedRoute>
                    <FichaVoo />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/sessoes/nova"
                element={
                  <ProtectedRoute>
                    <NovaSessao />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/cadastros/simuladores"
                element={
                  <ProtectedRoute>
                    <CrudSimuladores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/cadastros/manobras"
                element={
                  <ProtectedRoute>
                    <CrudManobras />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/cadastros/modelos"
                element={
                  <ProtectedRoute>
                    <CrudModelos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/cadastros/categorias"
                element={
                  <ProtectedRoute>
                    <CrudCategorias />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/cadastros/tipos"
                element={
                  <ProtectedRoute>
                    <CrudTiposSessao />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/cadastros/instrutores"
                element={
                  <ProtectedRoute>
                    <CrudInstrutores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/cadastros/modelos-sessao"
                element={
                  <ProtectedRoute>
                    <CrudModelosSessao />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simuladores/relatorios"
                element={
                  <ProtectedRoute>
                    <RelatoriosSimuladores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuracoes"
                element={
                  <ProtectedRoute>
                    <Configuracoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuracoes/cadastros"
                element={
                  <ProtectedRoute>
                    <ConfiguracoesCadastrosGerais />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuracoes/compliance"
                element={
                  <ProtectedRoute>
                    <ComplianceSettings />
                  </ProtectedRoute>
                }
              />
              {/* Importação Inteligente */}
              <Route
                path="/importacao"
                element={
                  <ProtectedRoute>
                    <ImportacaoPageV2 />
                  </ProtectedRoute>
                }
              />
              {/* Compat: redirecionar rota antiga para a nova */}
              <Route path="/habilitacoes" element={<Navigate to="/qualificacoes" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
